import { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import images from "../../utilities/images/images";
import ShakaSolutionViewVideoPlayer from "../../custom_utilities/shakaVideoPlayer/ShakaSolutionViewVideoPlayer";
import {
  normalizeMistakeTopicQuestion,
  saveMistakeBook,
} from "./mistakeBookApi";
import checkIcon from "./assets/mb-check.svg";
import crossIcon from "./assets/mb-cross.svg";
import resolvedIcon from "./assets/mb-resolved.svg";
import chevronDownIcon from "./assets/mb-chevron-down.svg";
import popupCloseIcon from "./assets/mb-popup-close.svg";
import "./MistakeBookQuestionDetailPage.css";

const MISTAKE_TAGS = [
  "Conceptual Gap",
  "Silly Mistake",
  "Formula Error",
  "Calculation Error",
  "Time Pressure",
  "Guessed",
];

const hasVideoId = (videoId) => {
  if (videoId === null || videoId === undefined) return false;
  const raw = String(videoId).trim();
  return raw !== "" && raw !== "0";
};

const getDifficultyTone = (label = "") => {
  const key = String(label).trim().toLowerCase();
  if (key === "easy") return "easy";
  if (key === "medium" || key === "moderate") return "medium";
  if (key === "hard" || key === "tough" || key === "difficult") return "hard";
  return "default";
};

const formatDifficultyLabel = (label = "") => {
  const raw = String(label || "").trim();
  if (!raw) return "";
  return raw.charAt(0).toUpperCase() + raw.slice(1);
};

const MistakeBookQuestionDetailPage = () => {
  const history = useHistory();
  const location = useLocation();
  const state = location.state || {};

  const topicName = state.topicName || state.topic?.name || "Mistake Book";
  const subjectName = state.subjectName || state.subject?.name || "";
  const year = state.year;
  const subjectId = state.subjectId || state.subject?.id;
  const topic = state.topic || {};
  const listPath =
    state.listPath || "/practice/mistake-book/topic-questions";
  const listState = {
    topic,
    subject: state.subject,
    subjectName,
    topicName: state.topicName || topic.name || "",
    year,
    subjectId,
    activeTab: state.activeTab || "mistakes",
  };

  const initialQuestions = useMemo(() => {
    const source = Array.isArray(state.questions)
      ? state.questions
      : state.question
        ? [state.question]
        : [];
    return source.map((item, index) =>
      item?.options
        ? { ...item, number: item.number || index + 1 }
        : normalizeMistakeTopicQuestion(item?.raw || item, index)
    );
  }, [state.questions, state.question]);

  const initialIndex = useMemo(() => {
    if (!initialQuestions.length) return 0;
    if (Number.isFinite(Number(state.questionIndex))) {
      const idx = Number(state.questionIndex);
      return Math.max(0, Math.min(idx, initialQuestions.length - 1));
    }
    const qid = state.qid ?? state.question?.qid ?? state.question?.id;
    if (qid == null) return 0;
    const found = initialQuestions.findIndex(
      (q) => String(q.qid) === String(qid) || String(q.id) === String(qid)
    );
    return found >= 0 ? found : 0;
  }, [initialQuestions, state.qid, state.question, state.questionIndex]);

  const [questions, setQuestions] = useState(initialQuestions);
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [solutionTab, setSolutionTab] = useState("text");
  const [tagOpen, setTagOpen] = useState(false);
  const [unresolveOpen, setUnresolveOpen] = useState(false);
  const [draftTag, setDraftTag] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [confirmationTag, setConfirmationTag] = useState("");
  const confirmationTimerRef = useRef(null);

  useEffect(() => {
    setQuestions(initialQuestions);
    setActiveIndex(initialIndex);
  }, [initialQuestions, initialIndex]);

  useEffect(() => {
    setSolutionTab("text");
    setTagOpen(false);
    setUnresolveOpen(false);
    setDraftTag("");
    setSaveError("");
  }, [activeIndex]);

  useEffect(() => {
    if (!tagOpen && !unresolveOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        if (tagOpen) setTagOpen(false);
        if (unresolveOpen) setUnresolveOpen(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [tagOpen, unresolveOpen]);

  useEffect(
    () => () => {
      if (confirmationTimerRef.current) {
        clearTimeout(confirmationTimerRef.current);
      }
    },
    []
  );

  const showTagConfirmation = (tag) => {
    if (confirmationTimerRef.current) {
      clearTimeout(confirmationTimerRef.current);
    }
    setConfirmationTag(tag);
    confirmationTimerRef.current = setTimeout(() => {
      setConfirmationTag("");
      confirmationTimerRef.current = null;
    }, 3000);
  };

  const question = questions[activeIndex] || null;
  const hasVideo = hasVideoId(question?.videoId);
  const questionNumber = question?.number || activeIndex + 1;
  const difficultyLabel = formatDifficultyLabel(question?.difficulty);
  const difficultyTone = getDifficultyTone(question?.difficulty);

  const handleBack = () => {
    history.push(listPath || "/practice/mistake-book/topic-questions", listState);
  };

  const handleFinish = () => {
    history.push("/practice/mistake-book/success", {
      year,
      subjectId,
      subjectName,
      subject: state.subject,
      topic,
      topicName,
      listPath,
      listState,
      questions,
      questionIndex: activeIndex,
      fromMistakeBook: true,
    });
  };

  const handleSelectQuestion = (index) => {
    if (index < 0 || index >= questions.length) return;
    setActiveIndex(index);
  };

  const openTagModal = () => {
    setDraftTag(question?.mistakeTag || "");
    setSaveError("");
    setTagOpen(true);
  };

  const closeTagModal = () => {
    setTagOpen(false);
    setDraftTag("");
  };

  const closeUnresolveModal = () => {
    setUnresolveOpen(false);
  };

  const handleResolveClick = () => {
    if (saving) return;
    if (question?.isResolved) {
      setSaveError("");
      setUnresolveOpen(true);
      return;
    }
    handleMarkResolved();
  };

  const persistMistake = async ({ mistakeTag, isResolved } = {}) => {
    if (!question?.qid) {
      setSaveError("Question id is missing.");
      return false;
    }

    setSaving(true);
    setSaveError("");
    try {
      await saveMistakeBook({
        qid: question.qid,
        question_id: question.qid,
        subject: subjectName,
        topic_name: listState.topicName || topicName,
        mistake_tag: mistakeTag ?? question.mistakeTag ?? "",
        is_resolved:
          isResolved === undefined
            ? question.isResolved
              ? 1
              : 0
            : isResolved
              ? 1
              : 0,
        resolved: isResolved === undefined ? question.isResolved : isResolved,
      });
      return true;
    } catch (err) {
      setSaveError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to save. Please try again."
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleApplyTag = async () => {
    if (!draftTag) return;
    const appliedTag = draftTag;
    const ok = await persistMistake({
      mistakeTag: appliedTag,
      isResolved: question?.isResolved,
    });
    if (!ok) return;
    setQuestions((prev) =>
      prev.map((item, index) =>
        index === activeIndex ? { ...item, mistakeTag: appliedTag } : item
      )
    );
    closeTagModal();
    showTagConfirmation(appliedTag);
  };

  const handleMarkResolved = async () => {
    if (question?.isResolved) return;
    const ok = await persistMistake({
      mistakeTag: question?.mistakeTag,
      isResolved: true,
    });
    if (!ok) return;
    setQuestions((prev) =>
      prev.map((item, index) =>
        index === activeIndex ? { ...item, isResolved: true } : item
      )
    );
  };

  const handleConfirmUnresolve = async () => {
    if (!question?.isResolved) {
      closeUnresolveModal();
      return;
    }
    const ok = await persistMistake({
      mistakeTag: question?.mistakeTag,
      isResolved: false,
    });
    if (!ok) return;
    setQuestions((prev) =>
      prev.map((item, index) =>
        index === activeIndex ? { ...item, isResolved: false } : item
      )
    );
    closeUnresolveModal();
  };

  if (!question) {
    return (
      <div className="mb-qd-page">
        <header className="mb-qd-header">
          <button
            type="button"
            className="mb-qd-header__back"
            onClick={handleBack}
            aria-label="Go back"
          >
            <img src={images.new_backarrow} alt="" />
          </button>
          <h1 className="mb-qd-header__title">{topicName}</h1>
          <div className="mb-qd-header__spacer" aria-hidden="true" />
        </header>
        <div className="mb-qd-empty">
          <p>Question details are unavailable.</p>
          <button type="button" className="mb-qd-empty__btn" onClick={handleBack}>
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-qd-page">
      <header className="mb-qd-header">
        <button
          type="button"
          className="mb-qd-header__back"
          onClick={handleBack}
          aria-label="Go back"
        >
          <img src={images.new_backarrow} alt="" />
        </button>
        <h1 className="mb-qd-header__title">{topicName}</h1>
        <div className="mb-qd-header__spacer" aria-hidden="true" />
      </header>

      <div className="mb-qd-body">
        <div className="mb-qd-main">
          <div className="mb-qd-meta">
            <div className="mb-qd-meta__left">
              <span className="mb-qd-meta__qno">Q. {questionNumber}</span>
              {difficultyLabel ? (
                <>
                  <span className="mb-qd-meta__divider" aria-hidden="true" />
                  <span
                    className={`mb-qd-meta__difficulty is-${difficultyTone}`}
                  >
                    {difficultyLabel}
                  </span>
                </>
              ) : null}
            </div>
          </div>

          <div
            className="mb-qd-question"
            dangerouslySetInnerHTML={{ __html: question.text || "" }}
          />

          <div className="mb-qd-options">
            {(question.options || []).map((opt) => {
              const isCorrect = Boolean(opt.isCorrect);
              const isUserWrong = Boolean(opt.isUserAnswer) && !isCorrect;
              const showCorrectLabel = isCorrect;
              const showYourAnswer = isUserWrong;

              return (
                <div
                  key={`${question.id}-${opt.id}-${opt.label}`}
                  className={`mb-qd-option${
                    isCorrect ? " is-correct" : ""
                  }${isUserWrong ? " is-wrong" : ""}`}
                >
                  <div className="mb-qd-option__row">
                    <span className="mb-qd-option__label">{opt.label}</span>
                    <span
                      className="mb-qd-option__text"
                      dangerouslySetInnerHTML={{ __html: opt.text || "" }}
                    />
                  </div>
                  {showYourAnswer ? (
                    <span className="mb-qd-option__badge is-wrong">
                      <img src={crossIcon} alt="" />
                      Your Answer
                    </span>
                  ) : null}
                  {showCorrectLabel ? (
                    <span className="mb-qd-option__badge is-correct">
                      <img src={checkIcon} alt="" />
                      Correct answer
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>

          <section className="mb-qd-solution">
            <div className="mb-qd-solution__header">
              <h2 className="mb-qd-solution__title">Solution :</h2>
              <div className="mb-qd-solution__tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={solutionTab === "text"}
                  className={`mb-qd-solution__tab${
                    solutionTab === "text" ? " is-active" : ""
                  }`}
                  onClick={() => setSolutionTab("text")}
                >
                  Text
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={solutionTab === "video"}
                  className={`mb-qd-solution__tab${
                    solutionTab === "video" ? " is-active" : ""
                  }`}
                  onClick={() => setSolutionTab("video")}
                  disabled={!hasVideo}
                >
                  Video
                </button>
              </div>
            </div>

            {solutionTab === "text" ? (
              <div
                className="mb-qd-solution__box"
                dangerouslySetInnerHTML={{
                  __html:
                    question.solutionText ||
                    "<p>Solution not available for this question.</p>",
                }}
              />
            ) : hasVideo ? (
              <div className="mb-qd-solution__video">
                <ShakaSolutionViewVideoPlayer
                  key={`${question.videoId}-${question.qid}`}
                  id_video={question.videoId}
                  className="shakaPlayer"
                />
              </div>
            ) : (
              <div className="mb-qd-solution__box">
                <p>Video solution is not available for this question.</p>
              </div>
            )}
          </section>

          <div className="mb-qd-nav">
            <button
              type="button"
              className="mb-qd-nav__btn mb-qd-nav__btn--prev"
              disabled={activeIndex <= 0}
              onClick={() => handleSelectQuestion(activeIndex - 1)}
            >
              Previous
            </button>
            {activeIndex >= questions.length - 1 ? (
              <button
                type="button"
                className="mb-qd-nav__btn mb-qd-nav__btn--primary"
                onClick={handleFinish}
              >
                Finish
              </button>
            ) : (
              <button
                type="button"
                className="mb-qd-nav__btn mb-qd-nav__btn--primary"
                onClick={() => handleSelectQuestion(activeIndex + 1)}
              >
                Next
              </button>
            )}
          </div>
        </div>

        <aside className="mb-qd-aside">
          <div className="mb-qd-aside__top">
            <p className="mb-qd-aside__label">Mistakes</p>
            <div className="mb-qd-pills">
              {questions.map((item, index) => (
                <button
                  type="button"
                  key={item.id || item.qid || index}
                  className={`mb-qd-pill${
                    index === activeIndex ? " is-active" : ""
                  }`}
                  onClick={() => handleSelectQuestion(index)}
                >
                  {item.number || index + 1}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-qd-aside__actions">
            {saveError && !tagOpen && !unresolveOpen ? (
              <p className="mb-qd-aside__error">{saveError}</p>
            ) : null}

            <button
              type="button"
              className="mb-qd-tag-btn"
              onClick={openTagModal}
            >
              <span>{question.mistakeTag || "Tag Your Mistake"}</span>
              <span className="mb-qd-tag-btn__icon" aria-hidden="true">
                <img src={chevronDownIcon} alt="" />
              </span>
            </button>

            <button
              type="button"
              className={`mb-qd-resolve${
                question.isResolved ? " is-done" : ""
              }`}
              onClick={handleResolveClick}
              disabled={saving && !unresolveOpen}
            >
              {saving && !tagOpen && !unresolveOpen ? (
                <ClipLoader size={18} color="#fff" />
              ) : (
                <img src={resolvedIcon} alt="" />
              )}
              <span>
                {question.isResolved ? "Resolved" : "Mark as resolved"}
              </span>
            </button>
          </div>
        </aside>
      </div>

      {tagOpen ? (
        <div
          className="mb-qd-tag-overlay"
          role="presentation"
          onClick={() => !saving && closeTagModal()}
        >
          <div
            className="mb-qd-tag-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mb-qd-tag-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="mb-qd-tag-modal__close"
              onClick={closeTagModal}
              disabled={saving}
              aria-label="Close"
            >
              <img src={popupCloseIcon} alt="" />
            </button>

            <div className="mb-qd-tag-modal__body">
              <h3 id="mb-qd-tag-modal-title">Tag Your Mistake</h3>

              <div className="mb-qd-tag-modal__content">
                <p className="mb-qd-tag-modal__prompt">
                  Why did you get this wrong?
                </p>

                <div className="mb-qd-tag-modal__grid" role="radiogroup">
                  {MISTAKE_TAGS.map((tag) => {
                    const selected = draftTag === tag;
                    return (
                      <button
                        type="button"
                        key={tag}
                        role="radio"
                        aria-checked={selected}
                        className={`mb-qd-tag-modal__option${
                          selected ? " is-selected" : ""
                        }`}
                        onClick={() => setDraftTag(tag)}
                      >
                        <span
                          className="mb-qd-tag-modal__radio"
                          aria-hidden="true"
                        />
                        <span>{tag}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {saveError ? (
                <p className="mb-qd-aside__error">{saveError}</p>
              ) : null}

              <div className="mb-qd-tag-modal__actions">
                <button
                  type="button"
                  className="mb-qd-tag-modal__cancel"
                  onClick={closeTagModal}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="mb-qd-tag-modal__apply"
                  onClick={handleApplyTag}
                  disabled={saving || !draftTag}
                >
                  {saving ? <ClipLoader size={18} color="#fff" /> : "Apply Tag"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {unresolveOpen ? (
        <div
          className="mb-qd-tag-overlay"
          role="presentation"
          onClick={() => !saving && closeUnresolveModal()}
        >
          <div
            className="mb-qd-unresolve-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mb-qd-unresolve-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="mb-qd-tag-modal__close"
              onClick={closeUnresolveModal}
              disabled={saving}
              aria-label="Close"
            >
              <img src={popupCloseIcon} alt="" />
            </button>

            <div className="mb-qd-unresolve-modal__body">
              <h3 id="mb-qd-unresolve-modal-title">Mark as Unresolved?</h3>
              <p>
                These questions will be added back to your Mistake Book for
                revision.
              </p>

              {saveError ? (
                <p className="mb-qd-aside__error">{saveError}</p>
              ) : null}

              <div className="mb-qd-unresolve-modal__actions">
                <button
                  type="button"
                  className="mb-qd-unresolve-modal__cancel"
                  onClick={closeUnresolveModal}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="mb-qd-unresolve-modal__confirm"
                  onClick={handleConfirmUnresolve}
                  disabled={saving}
                >
                  {saving ? <ClipLoader size={18} color="#fff" /> : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {confirmationTag ? (
        <div className="mb-qd-tag-toast" role="status" aria-live="polite">
          <span className="mb-qd-tag-toast__accent" aria-hidden="true" />
          <p className="mb-qd-tag-toast__text">
            Mistake tagged as{" "}
            <em>&ldquo;{confirmationTag}&rdquo;</em>
          </p>
        </div>
      ) : null}
    </div>
  );
};

export default MistakeBookQuestionDetailPage;
